import { SettingsIcon } from "@xola/icons";
import React, { ReactNode } from "react";
import { useHistory } from "react-router-dom";
import { Plugin } from "types";
import { useInstalledPlugins, usePlugins } from "../../../hooks/usePlugins";
import { NoTranslate } from "../../Common/NoTranslate";
import { PluginImage } from "../../Common/PluginImage";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { ContentLayout } from "../../Layout/ContentLayout";

export const XolaBotPluginList = () => {
    const history = useHistory();
    const { data: plugins = [] } = usePlugins();
    const { data: installedPlugins, isLoading } = useInstalledPlugins();

    const installedXolaBotPlugins = installedPlugins.filter((plugin) => {
        return plugin.abilities && "xolabot" in plugin.abilities;
    });

    const uninstalledXolaBotPlugins = plugins.filter((plugin) => {
        return (
            plugin.abilities &&
            "xolabot" in plugin.abilities &&
            !installedXolaBotPlugins.some(({ id }) => id === plugin.id)
        );
    });

    const handleConfigureClick = (plugin: Plugin) => () => {
        history.push(`/settings/apps/${plugin.id}`);
    };

    const handleInstallClick = (plugin: Plugin) => () => {
        history.push(`/apps/${plugin.id}`);
    };

    return (
        <ContentLayout isWide className="space-y-8" isDisabled={isLoading}>
            <h2>Apps</h2>

            <ContentLayout.Section title="Your apps" description="Install and configure XolaBots">
                <PluginList title="Installed" plugins={installedXolaBotPlugins}>
                    {(plugin) =>
                        plugin.config.length > 0 ? (
                            <SecondaryButton size="small" onClick={handleConfigureClick(plugin)}>
                                <SettingsIcon />
                            </SecondaryButton>
                        ) : null
                    }
                </PluginList>

                <PluginList title="Not Installed" plugins={uninstalledXolaBotPlugins}>
                    {(plugin) => (
                        <SecondaryButton size="small" onClick={handleInstallClick(plugin)}>
                            Install
                        </SecondaryButton>
                    )}
                </PluginList>
            </ContentLayout.Section>
        </ContentLayout>
    );
};

interface PluginListProps {
    title: string;
    plugins: Plugin[];
    children: (plugin: Plugin) => ReactNode;
}

const PluginList = ({ title, plugins, children }: PluginListProps) => {
    if (plugins.length === 0) {
        return null;
    }

    return (
        <>
            <h3>{title}</h3>

            <div>
                {plugins.map((plugin) => (
                    <PluginItem key={plugin.id} plugin={plugin}>
                        {children(plugin)}
                    </PluginItem>
                ))}
            </div>
        </>
    );
};

interface PluginItemProps {
    plugin: Plugin;
    children: ReactNode;
}

const PluginItem = ({ plugin, children }: PluginItemProps) => {
    return (
        <div className="flex items-center border-b border-b-gray-lighter py-4">
            <PluginImage plugin={plugin} className="mr-4.5 h-12 w-12" />

            <div className="flex-1">
                <NoTranslate as="h4" className="font-bold">
                    {plugin.name}
                </NoTranslate>
                <NoTranslate as="div" className="text-gray-darker">
                    {plugin.summary}
                </NoTranslate>
            </div>

            {children}
        </div>
    );
};
